import { CSSProperties, useEffect, useMemo, useState } from "react";
import s from "./VideoPlayer.module.scss";

interface Props {
  videoId?: string | null;
  className?: string;
  isVertical?: boolean;
}

export default function VideoPlayer({
  videoId,
  className,
  isVertical = false,
}: Props) {
  const [error, setError] = useState(false);
  const [videoRatio, setVideoRatio] = useState<number>(isVertical ? 9 / 16 : 16 / 9);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);

  const hasVideo = Boolean(videoId) && !error;
  const src = videoId ? `/videos/${videoId}` : "";

  const isActuallyVertical = useMemo(() => {
    return isVertical || videoRatio < 1;
  }, [isVertical, videoRatio]);

  useEffect(() => {
    if (!isActuallyVertical) {
      setBoxWidth(null);
      return;
    }

    const recalc = () => {
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      // Резервируем место под шапку, отступы страницы, текст и controls
      const reservedHeight = viewportW <= 640 ? 210 : 260;

      // Небольшие боковые отступы, чтобы не упираться в края
      const sideGutter = viewportW <= 640 ? 24 : 48;

      const maxWidthByViewport = Math.max(280, viewportW - sideGutter);
      const maxHeightForVideo = Math.max(420, viewportH - reservedHeight);

      // Ширина, при которой весь вертикальный ролик помещается по высоте
      const widthByHeight = maxHeightForVideo * videoRatio;

      // Верхняя граница, чтобы на больших экранах ролик не становился слишком массивным
      const safeMaxWidth = viewportW <= 640 ? 420 : 560;

      const nextWidth = Math.min(maxWidthByViewport, widthByHeight, safeMaxWidth);

      setBoxWidth(Math.floor(nextWidth));
    };

    recalc();
    window.addEventListener("resize", recalc);

    return () => {
      window.removeEventListener("resize", recalc);
    };
  }, [isActuallyVertical, videoRatio]);

  const dynamicStyle: CSSProperties = isActuallyVertical
    ? {
        width: boxWidth ? `${boxWidth}px` : undefined,
        maxWidth: "100%",
        aspectRatio: String(videoRatio),
      }
    : {
        aspectRatio: String(videoRatio || 16 / 9),
      };

  return (
    <div
      className={[
        s.videoBox,
        isActuallyVertical ? s.videoBoxVertical : s.videoBoxHorizontal,
        className || "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={dynamicStyle}
    >
      {hasVideo ? (
        <video
          className={s.video}
          controls
          playsInline
          preload="metadata"
          src={src}
          onError={() => setError(true)}
          onLoadedMetadata={(e) => {
            const el = e.currentTarget;
            const width = el.videoWidth;
            const height = el.videoHeight;

            if (width > 0 && height > 0) {
              setVideoRatio(width / height);
            }
          }}
          controlsList="nodownload"
          disablePictureInPicture
        />
      ) : (
        <div className={s.placeholder}>
          <div className={s.play} />
          <div className={s.note}>Видео появится позже</div>
        </div>
      )}
    </div>
  );
}
